/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, useTheme } from "@mui/material";
import { HeatSeries } from "src/store/api/generatedApi";
import { getDiffColor, getSourceValue } from "../utils";
import { roundToDecimalPlaces } from "hooks/serialisers/numbers";

interface CostSparklineProps {
  row: HeatSeries;
  sources: string[];
  selectedSummaryName: string | null;
  ignoredMaterials: Set<string>;
  width: number;
  height: number;
}

export const CostSparkline = ({
  row,
  sources,
  selectedSummaryName,
  ignoredMaterials,
  width,
  height,
}: CostSparklineProps) => {
  const theme = useTheme();

  if (!selectedSummaryName || sources.length < 2) return null;

  const allCosts = sources.map((source) => {
    return getSourceValue(
      source,
      row.mixes,
      selectedSummaryName,
      ignoredMaterials
    );
  });

  const validCosts = allCosts.filter((cost) => cost !== null);

  if (validCosts.length === 0) return null;

  const firstValue = validCosts[0];
  const lastValue = validCosts[validCosts.length - 1];
  const difference = roundToDecimalPlaces(
    (lastValue?.value ?? 0) - (firstValue?.value ?? 0),
    lastValue?.decimalPlaces ?? 0
  );

  const maxPossibleRange =
    Math.max(firstValue?.value ?? 0, lastValue?.value ?? 0) * 0.8;
  const effectiveRange = Math.max(difference * 2, maxPossibleRange * 0.1);

  const midPoint = ((firstValue?.value ?? 0) + (lastValue?.value ?? 0)) / 2;
  const scaledMin = midPoint - effectiveRange / 2;
  const scaledMax = midPoint + effectiveRange / 2;

  const glowColor = getDiffColor(
    difference,
    lastValue?.value ?? null,
    selectedSummaryName ?? "cost",
    theme,
    0.8
  );

  const padding = 2;

  const coordinates = allCosts.map((cost, index) => {
    const x =
      padding + (index * (width - 2 * padding)) / (sources.length - 1 || 1);
    if (cost === null) return { x, y: null };
    const normalizedCost = (cost.value - scaledMin) / (scaledMax - scaledMin);
    const clampedNormalized = Math.max(0, Math.min(1, normalizedCost));
    const y = height - padding - clampedNormalized * (height - 2 * padding);
    return { x, y };
  });

  const nonNullPoints = coordinates.flatMap((coord, i) =>
    coord.y !== null ? [{ x: coord.x, y: coord.y, sourceIndex: i }] : []
  );

  const dashedSegments = nonNullPoints.flatMap((point, i) => {
    if (i === 0) return [];
    const prev = nonNullPoints[i - 1];
    if (!prev || point.sourceIndex - prev.sourceIndex <= 1) return [];
    return [
      {
        key: `${sources[prev.sourceIndex] ?? ""}-${sources[point.sourceIndex] ?? ""}`,
        x1: prev.x,
        y1: prev.y,
        x2: point.x,
        y2: point.y,
      },
    ];
  });

  const solidSegments = nonNullPoints.reduce<
    { key: string; points: string[] }[]
  >((segments, { x, y, sourceIndex }, i) => {
    const point = `${x},${y}`;
    const prevPoint = i > 0 ? nonNullPoints[i - 1] : undefined;
    const lastSegment = segments[segments.length - 1];
    if (prevPoint && lastSegment && sourceIndex === prevPoint.sourceIndex + 1) {
      return [
        ...segments.slice(0, -1),
        { key: lastSegment.key, points: [...lastSegment.points, point] },
      ];
    }
    return [...segments, { key: sources[sourceIndex] ?? "", points: [point] }];
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <svg width={width} height={height}>
        {solidSegments.map((segment) => (
          <polyline
            key={`solid-${segment.key}`}
            points={segment.points.join(" ")}
            fill="none"
            stroke={glowColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#glow)"
          />
        ))}
        {dashedSegments.map((seg) => (
          <line
            key={`dashed-${seg.key}`}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            stroke={glowColor}
            strokeWidth="1.5"
            strokeDasharray="5 3"
            opacity={0.5}
          />
        ))}
        {coordinates.map((coord, index) =>
          coord.y !== null ? (
            <circle
              key={`sparkline-${sources[index] ?? ""}`}
              cx={coord.x}
              cy={coord.y}
              r="2"
              fill={glowColor}
              filter="url(#glow)"
            />
          ) : null
        )}
      </svg>
    </Box>
  );
};
